import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';
import * as ws from '../../features/workspaces/api';
import type { Role } from '../../features/workspaces/api';
import { changePassword, deleteAccount, readProfile, savePrefs, updateProfile, type ProfileExtras } from '../../auth/passwordSession';
import { toast } from '../ui/toast';
import { invalidateCatalogue } from './freshness';
import { useUpload } from '../upload/store';

/** The Account and Workspace pages' reads and writes. */
export interface UsageImage {
  image_id: string;
  /** Selects this image in the viewer (`?series=`). */
  series_instance_uid: string;
  /** The study's public id. */
  study: string;
  kind: 'wsi' | 'flat' | 'document';
  original_filename: string | null;
  description: string | null;
  created_utc: string;
  /** False while the image converts or awaits its details. */
  published: boolean;
  study_id: string | null;
  study_description: string | null;
  collection_id: string | null;
  collection_name: string | null;
  group_name: string | null;
  workspace_id: string | null;
  bytes: number;
}

/** A workspace the caller owns, and what it holds of their storage. */
export interface UsageWorkspace {
  public_id: string;
  name: string;
  /** Being deleted: still stored, and counted, until its contents are gone. */
  archived: boolean;
  usedBytes: number;
  pendingBytes: number;
}

/**
 * The caller's storage: the workspaces they OWN, whoever uploaded to them.
 * `workspaces` largest first; `images` those in them, largest first.
 */
export interface Usage {
  limitBytes: number;
  usedBytes: number;
  /** Uploads under way: not stored yet, but counted when a new upload asks for room. */
  pendingBytes?: number;
  workspaces: UsageWorkspace[];
  images: UsageImage[];
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${getConfig().apiRoot}${path}`, { credentials: 'include', headers: init?.body ? { 'content-type': 'application/json' } : {}, ...init });
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new HttpError(body.error || `Request failed (${res.status})`, res.status);
  return body as T;
}

const fail = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong');

/** "1.2 GB", "340 MB": the prototype's fmt(), from bytes. */
export function formatBytes(bytes: number): string {
  const mb = bytes / 1024 ** 2;
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb >= 10240 ? 0 : 1)} GB`;
  return `${Math.round(mb)} MB`;
}

export function useProfile() {
  return useQuery({ queryKey: ['profile'], queryFn: readProfile });
}

export function useUsage(enabled = true) {
  return useQuery({ queryKey: ['usage'], queryFn: () => call<Usage>('/usage'), enabled });
}

export function useAccountMutations() {
  const qc = useQueryClient();
  const session = () => {
    qc.invalidateQueries({ queryKey: ['session'] });
    qc.invalidateQueries({ queryKey: ['profile'] });
    // A name change shows on every author chip and comment.
    void invalidateCatalogue(qc);
    qc.invalidateQueries({ queryKey: ['comments'] });
  };
  return {
    deleteImages: useMutation({
      mutationFn: (imageIds: string[]) => call<{ deleted: number; bytes: number }>('/images/delete', { method: 'POST', body: JSON.stringify({ imageIds }) }),
      onSuccess: (r) => {
        void invalidateCatalogue(qc);
        toast(`Freed ${formatBytes(r.bytes)}`);
        // Room was made: uploads refused for want of it go now, if they fit.
        void useUpload.getState().retryRefused();
      },
      onError: fail,
    }),
    saveProfile: useMutation({
      mutationFn: (v: { givenName: string; familyName: string } & ProfileExtras) => updateProfile(v.givenName, v.familyName, { title: v.title, institution: v.institution, linkedin: v.linkedin, roleTitle: v.roleTitle }),
      onSuccess: () => {
        session();
        toast('Profile saved');
      },
      onError: fail,
    }),
    changePassword: useMutation({
      mutationFn: (v: { current: string; next: string }) => changePassword(v.current, v.next),
      onSuccess: () => toast('Password updated'),
      onError: fail,
    }),
    savePrefs: useMutation({
      mutationFn: (prefs: Record<string, boolean>) => savePrefs(prefs),
      onSuccess: session,
      onError: fail,
    }),
    deleteAccount: useMutation({
      mutationFn: (password: string | undefined) => deleteAccount(password),
      onError: fail,
    }),
  };
}

export function useMembers(workspaceId: string | undefined) {
  return useQuery({ queryKey: ['members', workspaceId], queryFn: () => ws.listMembers(workspaceId!), enabled: !!workspaceId });
}

export function useWorkspaceMutations() {
  const qc = useQueryClient();
  const done = (id?: string) => {
    qc.invalidateQueries({ queryKey: ['workspaces'] });
    if (id) qc.invalidateQueries({ queryKey: ['members', id] });
    // Home names the workspace; a new workspace brings its default collection.
    void invalidateCatalogue(qc);
  };
  return {
    create: useMutation({ mutationFn: (name: string) => ws.createWorkspace(name), onSuccess: () => done(), onError: fail }),
    rename: useMutation({ mutationFn: (v: { id: string; name: string }) => ws.renameWorkspace(v.id, v.name), onSuccess: () => done(), onError: fail }),
    makeDefault: useMutation({ mutationFn: (id: string) => ws.setDefaultWorkspace(id), onSuccess: () => done(), onError: fail }),
    invite: useMutation({
      mutationFn: (v: { id: string; email: string; role: Role }) => ws.addMember(v.id, v.email, v.role),
      onSuccess: (r, v) => {
        done(v.id);
        toast('invited' in r ? `Invite sent to ${v.email}` : `${r.display_name} added`);
      },
      onError: fail,
    }),
    setRole: useMutation({ mutationFn: (v: { id: string; userId: string; role: Role }) => ws.setMemberRole(v.id, v.userId, v.role), onSuccess: (_r, v) => done(v.id), onError: fail }),
    remove: useMutation({ mutationFn: (v: { id: string; userId: string }) => ws.removeMember(v.id, v.userId), onSuccess: (_r, v) => done(v.id), onError: fail }),
    revoke: useMutation({ mutationFn: (v: { id: string; inviteId: string }) => ws.revokeInvitation(v.id, v.inviteId), onSuccess: (_r, v) => done(v.id), onError: fail }),
    transfer: useMutation({
      mutationFn: (v: { id: string; userId: string; name: string }) => ws.transferOwnership(v.id, v.userId),
      onSuccess: (_r, v) => {
        done(v.id);
        // The workspace's storage moved to them: the Usage page and upload room change.
        qc.invalidateQueries({ queryKey: ['usage'] });
        toast(`${v.name} now owns this workspace`);
      },
      onError: fail,
    }),
  };
}
